import React from 'react';
import { motion } from 'framer-motion';
import { FaChalkboardTeacher, FaBook, FaAward, FaLightbulb } from 'react-icons/fa';
import SEO from '../components/SEO';

const AcademicsPage = () => {
  return (
    <>
      <SEO 
        title="Academic Programs | CBSE Curriculum | Vidya Vatika World School"
        description="Comprehensive CBSE curriculum from Nursery to Class 12. Focus on academics, sports, arts and holistic development at Vidya Vatika Panipat."
        keywords="CBSE curriculum Panipat, academic programs, school classes, best education Haryana, nursery to class 12"
        canonicalUrl="/academics"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">Academics</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Excellence in Education - CBSE Curriculum & Innovative Learning
            </p>
          </div>
        </section>

        {/* Curriculum Section */}
        <section id="curriculum" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
              Our Curriculum
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-blue-50 rounded-2xl p-8 hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FaBook className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-blue-900 text-center mb-4">Primary Wing</h3>
                <p className="text-gray-700 leading-relaxed">
                  Classes Pre-Nursery to 5th - Focus on foundational literacy, numeracy, and holistic development through activity-based learning.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-blue-50 rounded-2xl p-8 hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FaChalkboardTeacher className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-blue-900 text-center mb-4">Middle School</h3>
                <p className="text-gray-700 leading-relaxed">
                  Classes 6th to 8th - Building strong conceptual foundation with introduction to various subjects and skill development.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-blue-50 rounded-2xl p-8 hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FaAward className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-blue-900 text-center mb-4">Secondary Wing</h3>
                <p className="text-gray-700 leading-relaxed">
                  Classes 9th to 10th - Rigorous academic preparation with focus on board examinations and career guidance.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Assessment Section */}
        <section id="assessment" className="py-16 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
              Assessment Pattern
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-4">Formative Assessment (40%)</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Unit Tests and Quizzes</li>
                    <li>• Class participation</li>
                    <li>• Projects and Assignments</li>
                    <li>• Practical work</li>
                    <li>• Oral tests</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-4">Summative Assessment (60%)</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Half-Yearly Examination</li>
                    <li>• Annual Examination</li>
                    <li>• Board Examinations (Class 10)</li>
                    <li>• Comprehensive evaluation</li>
                    <li>• Theory and Practical</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Teaching Methodology */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
              Teaching Methodology
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: FaLightbulb, title: 'Smart Classes', desc: 'Digital classrooms with interactive panels' },
                { icon: FaBook, title: 'Activity-Based', desc: 'Learning by doing approach' },
                { icon: FaAward, title: 'Project Work', desc: 'Research and innovation focus' },
                { icon: FaChalkboardTeacher, title: 'Expert Faculty', desc: 'Highly qualified teachers' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6"
                >
                  <item.icon className="text-5xl text-blue-900 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AcademicsPage;
